import classNames from 'classnames'
import React, { ReactNode, useCallback, useEffect, useState } from 'react'

import { useDebouncedEffect } from 'core-components/hooks/useDebounced'
import { useFocus } from 'core-components/hooks/useFocus'
import { Input } from 'core-components/library/inputs'
import { Select } from 'core-components/library/selects'
import { ValidateInput } from 'core-components/library/validation'
import { Logic, Operator } from 'core-components/types'
import { Crud } from 'core/types/Crud'

import styles from './DropdownAutocomplete.module.scss'

export interface IDropdownAutocompleteProps<T extends IObjectAny> {
  inputClassName?: string
  caption?: string
  source: Crud<any>
  onChange: (value: string) => void
  onItemChange?: (value: T) => void
  label?: string
  translation?: string
  fieldsToSearch?: string[]
  calculateCaption?: (item: T) => ReactNode
  value: string | number
  checkFn?: ((value: string) => Promise<string>) | null
  validateName?: string
  margin?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  disabled?: boolean
  id?: string
  mode?: string
  autoFocus?: boolean
  onBlur?: () => void
  classes?: {
    input?: string
    root?: string
    popPaper?: string
  }
  placeholder?: string
  allowUnboundValue?: boolean
  minimumSearchLength?: number
}

export const DropdownAutocomplete = <T extends IObjectAny>({
  source,
  fieldsToSearch,
  calculateCaption,
  translation,
  onChange,
  onItemChange,
  label,
  caption,
  value,
  validateName,
  checkFn,
  margin,
  disabled,
  id,
  mode,
  onBlur: onBlurProps,
  autoFocus,
  classes,
  placeholder,
  minimumSearchLength = 0,
}: IDropdownAutocompleteProps<T>) => {
  const [inputed, setInputed] = useState<string>(value.toString() || '')
  const [searchValue, setSearchValue] = useState<string>(`${value}`)
  const { focused, onFocus, onBlur } = useFocus(0)
  const canShowOptions = searchValue.length >= minimumSearchLength

  const itemRenderFunction = caption
    ? (item: T) => item[caption]
    : (item: T) => calculateCaption && calculateCaption(item)

  useEffect(() => {
    if (value && source) {
      // When enter a symbol, such actions will greatly slow down the component. With combobox mode only change inputed
      if (mode !== 'combobox') {
        source.setFilter({
          filters: [],
          logic: Logic.AND,
        })
        source.getById(value).then((el) => {
          el ? setInputed(itemRenderFunction(el)) : setInputed(`${value}`)
          source.setFilter({
            filters: [
              {
                field: source.keyField,
                operator: Operator.NOTEQUAL,
                value: value,
              },
            ],
            logic: Logic.AND,
          })
        })
      } else {
        setInputed(`${value}`)
      }
    }
    !value && setInputed('')
  }, [value, source])

  const handleClick = (item: T) => {
    onBlur()
    // the key may not match the fieldToSearch
    if (mode === 'combobox' && fieldsToSearch && fieldsToSearch[0]) {
      onChange(item[fieldsToSearch[0]])
    } else {
      onChange(item[source.keyField])
    }
    onItemChange && onItemChange(item)
    onBlurProps && onBlurProps()
  }
  const onSearch = useCallback(() => {
    if (searchValue) {
      const filters = fieldsToSearch
        ? fieldsToSearch.map((field) => {
            return {
              field: field,
              operator: Operator.CONTAINS,
              value: searchValue,
            }
          })
        : [
            {
              field: caption || '',
              operator: Operator.CONTAINS,
              value: searchValue,
            },
          ]
      source.setFilter({
        logic: Logic.AND,
        filters: [
          {
            logic: Logic.OR,
            filters,
          },
        ],
      })
    } else {
      source.setFilter({
        logic: Logic.AND,
        filters: [],
      })
    }
    source.triggerOnChange()
    if (mode === 'combobox') {
      onChange(inputed)
    }
  }, [caption, fieldsToSearch, inputed, mode, onChange, searchValue, source])

  useDebouncedEffect(onSearch, 400, [])

  // useEffect(() => {
  //   // if (!focused && checkFn) {
  //   //   const editValue = inputProps.value
  //   //   !editValue && setInputed('')
  //   //   if (editValue) {
  //   //     source.getById(editValue).then((el) => {
  //   //       el ? setInputed(itemRenderFunction(el)) : setInputed(`${editValue}`)
  //   //       !el && onChange(editValue)
  //   //     })
  //   //   }
  //   // }
  // }, [focused])

  const inputProps = {
    autocompleteOff: true,
    disabled: disabled,
    value: inputed,
    translation,
    label: label,
    id: id,
    autoFocus: autoFocus,
    onChange: (text: string) => {
      if ((inputed || searchValue) && !text) {
        onChange('')
      }
      setInputed(text)
      setSearchValue(text)
      value = text
    },
    endContent: <i className={classNames(styles.arrow, focused ? styles['arrow--opened'] : '')} />,
    placeholder,
    placeholderTemplate: focused && !inputed && placeholder ? () => <>{placeholder}</> : undefined,
  }
  return (
    <Select<T>
      source={source}
      classes={{
        ...classes,
        popPaper: classNames(classes?.popPaper, {
          [styles.optionsHidden]: !canShowOptions,
        }),
      }}
      onClick={handleClick}
      disabled={disabled}
      renderFunction={itemRenderFunction}
    >
      {checkFn ? (
        <ValidateInput
          {...inputProps}
          classes={{
            validatorClasses: {
              root: classes?.input,
            },
          }}
          margin={margin}
          checkFn={checkFn}
          field={validateName || 'dropdownCheck'}
          onFocus={onFocus}
          onBlur={onBlur}
        />
      ) : (
        <Input
          {...inputProps}
          margin={margin}
          classes={{
            root: classes?.input,
          }}
          onFocus={onFocus}
          onBlur={onBlur}
        />
      )}
    </Select>
  )
}
